import { DetailHero } from "@/components/detail-hero";
import { Page } from "@/components/page-layout";
import { RequireScope } from "@/components/require-scope";
import { Heading } from "@/components/ui/Heading";
import {
  PageTabsTrigger,
  Tabs,
  TabsContent,
  PageTabsList,
} from "@/components/ui/Tabs";
import { Text } from "@/components/ui/Text";
import { remoteSessionScopeTier } from "@/lib/sources";
import { useRoutes } from "@/routes";
import { useOrganizationRemoteSessionIssuer } from "@gram/client/react-query/organizationRemoteSessionIssuer.js";
import { Link, Navigate, useLocation, useParams } from "react-router";
import { IssuerLogo, ScopeBadge } from "@/lib/remote-identity";
import { issuerDisplayName } from "./issuerDisplay";
import { ClientsTab } from "./tabs/issuer/ClientsTab";
import { OverviewTab } from "./tabs/issuer/OverviewTab";
import { SettingsTab } from "./tabs/issuer/SettingsTab";
import { activeDetailTab } from "@/lib/detail-tabs";
import { ISSUER_TABS, type IssuerTab } from "./tabs";

export default function RemoteIdentityProviderDetail(): JSX.Element {
  const { issuerId = "" } = useParams<{ issuerId: string }>();
  const routes = useRoutes();
  const location = useLocation();
  const {
    data: issuer,
    isLoading,
    isError,
  } = useOrganizationRemoteSessionIssuer({
    id: issuerId,
  });

  const activeTab = activeDetailTab(location.pathname, ISSUER_TABS);
  const tabHref = (tab: IssuerTab) =>
    routes.remoteIdentityProviders.issuerDetail[tab].href(issuerId);

  const label = issuer ? issuerDisplayName(issuer) : "Remote Identity Provider";

  // Platform issuers are read-only to tenants. The Settings tab holds the only
  // issuer mutation controls (edit + delete), both refused by the backend, so it
  // is hidden for a platform issuer; client management on the Clients tab stays.
  const isPlatform = !!issuer && remoteSessionScopeTier(issuer) === "platform";

  // The issuer doesn't exist (or failed to load); return to the listing.
  if (isError || (!isLoading && !issuer)) {
    return <Navigate to={routes.remoteIdentityProviders.href()} replace />;
  }

  // The bare /:issuerId URL has no tab; canonicalize to the Overview tab.
  if (!activeTab) {
    return <Navigate to={tabHref("overview")} replace />;
  }

  // A direct Settings URL on a platform issuer has no editable surface; send it
  // back to Overview.
  if (isPlatform && activeTab === "settings") {
    return <Navigate to={tabHref("overview")} replace />;
  }

  return (
    <Page>
      <Page.Header>
        <Page.Header.Breadcrumbs substitutions={{ [issuerId]: label }} />
      </Page.Header>
      <Page.Body fullWidth noPadding className="gap-0">
        <DetailHero>
          <Page.Eyebrow />
          <div className="flex items-center gap-3">
            <Text small muted>
              Remote Identity Provider
            </Text>
            {issuer && (
              <ScopeBadge
                projectId={issuer.projectId}
                organizationId={issuer.organizationId}
              />
            )}
          </div>
          <div className="flex items-center gap-3">
            {issuer && (
              <IssuerLogo logoAssetId={issuer.logoAssetId} name={label} />
            )}
            <Heading variant="h1" className="break-all normal-case">
              {label}
            </Heading>
          </div>
        </DetailHero>

        <RequireScope scope={["org:read", "org:admin"]} level="page">
          <Tabs value={activeTab} className="flex w-full flex-1 flex-col">
            <div className="shrink-0 border-b">
              <div className="mx-auto max-w-[1270px] px-8">
                <PageTabsList className="h-auto gap-6 bg-transparent p-0">
                  <PageTabsTrigger value="overview" asChild>
                    <Link to={tabHref("overview")}>Overview</Link>
                  </PageTabsTrigger>
                  <PageTabsTrigger value="clients" asChild>
                    <Link to={tabHref("clients")}>Clients</Link>
                  </PageTabsTrigger>
                  {!isPlatform && (
                    <PageTabsTrigger value="settings" asChild>
                      <Link to={tabHref("settings")}>Settings</Link>
                    </PageTabsTrigger>
                  )}
                </PageTabsList>
              </div>
            </div>

            <div className="mx-auto w-full max-w-[1270px] px-8 py-8">
              <TabsContent value="overview" className="mt-0">
                {issuer && <OverviewTab issuer={issuer} />}
              </TabsContent>
              <TabsContent value="clients" className="mt-0">
                {issuer && <ClientsTab issuer={issuer} />}
                {isLoading && <Text muted>Loading…</Text>}
              </TabsContent>
              {!isPlatform && (
                <TabsContent value="settings" className="mt-0">
                  {issuer && <SettingsTab key={issuer.id} issuer={issuer} />}
                  {isLoading && <Text muted>Loading…</Text>}
                </TabsContent>
              )}
            </div>
          </Tabs>
        </RequireScope>
      </Page.Body>
    </Page>
  );
}
